/**
 * Campaign Wizard Store
 *
 * Zustand store for managing campaign wizard state with localStorage persistence.
 */

import { v4 as uuidv4 } from 'uuid';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';
import { persist } from 'zustand/middleware';
import {
  CampaignWizardStep,
  CampaignWizardState,
  CampaignWizardData,
  WizardStepState,
  ValidationResult,
  CAMPAIGN_WIZARD_STEPS,
} from './wizard.types';

interface WizardStore {
  // Current wizard state
  activeWizard: CampaignWizardState | null;

  // Saved drafts
  drafts: CampaignWizardState[];

  // Actions
  startWizard: (type: 'recruiting' | 'sales') => void;
  cancelWizard: () => void;

  // Step navigation
  goToStep: (step: CampaignWizardStep) => void;
  nextStep: () => void;
  previousStep: () => void;

  // Step data management
  updateStepData: (step: CampaignWizardStep, data: Partial<CampaignWizardData>) => void;
  confirmStep: (step: CampaignWizardStep) => void;
  editStep: (step: CampaignWizardStep) => void;

  // Validation
  setValidation: (result: ValidationResult) => void;
  fixIssue: (issueId: string) => void;

  // Draft management
  saveDraft: () => void;
  loadDraft: (draftId: string) => void;
  deleteDraft: (draftId: string) => void;

  // Campaign creation
  setCampaignId: (campaignId: string) => void;
  completeWizard: () => void;

  // Utilities
  getCurrentStepIndex: () => number;
  getProgress: () => { current: number; total: number; percentage: number };
  isStepConfirmed: (step: CampaignWizardStep) => boolean;
  canProceed: () => boolean;
}

const getInitialStepState = (): Record<CampaignWizardStep, WizardStepState<CampaignWizardStep>> => {
  const steps: Record<string, WizardStepState<CampaignWizardStep>> = {};

  CAMPAIGN_WIZARD_STEPS.forEach((stepDef, index) => {
    steps[stepDef.id] = {
      step: stepDef.id,
      status: index === 0 ? 'active' : 'pending',
      data: {},
    };
  });

  return steps as Record<CampaignWizardStep, WizardStepState<CampaignWizardStep>>;
};

const getStepOrder = (): CampaignWizardStep[] => {
  return CAMPAIGN_WIZARD_STEPS.map(s => s.id);
};

const useWizardStore = createWithEqualityFn<WizardStore>()(
  persist(
    (set, get) => ({
      activeWizard: null,
      drafts: [],

      startWizard: (type) => {
        const wizard: CampaignWizardState = {
          id: uuidv4(),
          type,
          currentStep: 'type_selection',
          steps: getInitialStepState(),
          data: { campaignType: type },
          createdAt: new Date(),
          lastModifiedAt: new Date(),
        };

        // Pre-confirm type selection since it's already chosen
        wizard.steps.type_selection = {
          ...wizard.steps.type_selection,
          status: 'confirmed',
          data: { campaignType: type },
          confirmedAt: new Date(),
        };
        wizard.currentStep = 'name_account';
        wizard.steps.name_account.status = 'active';

        set({ activeWizard: wizard });
      },

      cancelWizard: () => {
        const { activeWizard } = get();
        if (activeWizard) {
          // Optionally save as draft before canceling
          // get().saveDraft();
        }
        set({ activeWizard: null });
      },

      goToStep: (step) => {
        const { activeWizard } = get();
        if (!activeWizard) return;

        const stepOrder = getStepOrder();
        const currentIndex = stepOrder.indexOf(activeWizard.currentStep);
        const targetIndex = stepOrder.indexOf(step);

        // Can only go back to confirmed steps or current step
        if (targetIndex > currentIndex) {
          // Check if all previous steps are confirmed
          for (let i = 0; i < targetIndex; i++) {
            const prevStep = stepOrder[i];
            const stepDef = CAMPAIGN_WIZARD_STEPS.find(s => s.id === prevStep);
            if (stepDef?.required && activeWizard.steps[prevStep].status !== 'confirmed') {
              return; // Cannot skip required unconfirmed steps
            }
          }
        }

        const updatedSteps = { ...activeWizard.steps };
        updatedSteps[activeWizard.currentStep] = {
          ...updatedSteps[activeWizard.currentStep],
          status: updatedSteps[activeWizard.currentStep].status === 'active'
            ? 'pending'
            : updatedSteps[activeWizard.currentStep].status,
        };
        updatedSteps[step] = {
          ...updatedSteps[step],
          status: 'active',
        };

        set({
          activeWizard: {
            ...activeWizard,
            currentStep: step,
            steps: updatedSteps,
            lastModifiedAt: new Date(),
          },
        });
      },

      nextStep: () => {
        const { activeWizard } = get();
        if (!activeWizard) return;

        const stepOrder = getStepOrder();
        const currentIndex = stepOrder.indexOf(activeWizard.currentStep);

        if (currentIndex < stepOrder.length - 1) {
          const nextStep = stepOrder[currentIndex + 1];
          get().goToStep(nextStep);
        }
      },

      previousStep: () => {
        const { activeWizard } = get();
        if (!activeWizard) return;

        const stepOrder = getStepOrder();
        const currentIndex = stepOrder.indexOf(activeWizard.currentStep);

        if (currentIndex > 0) {
          const prevStep = stepOrder[currentIndex - 1];
          get().goToStep(prevStep);
        }
      },

      updateStepData: (step, data) => {
        const { activeWizard } = get();
        if (!activeWizard) return;

        set({
          activeWizard: {
            ...activeWizard,
            data: { ...activeWizard.data, ...data },
            steps: {
              ...activeWizard.steps,
              [step]: {
                ...activeWizard.steps[step],
                data: { ...activeWizard.steps[step].data, ...data },
              },
            },
            lastModifiedAt: new Date(),
          },
        });
      },

      confirmStep: (step) => {
        const { activeWizard } = get();
        if (!activeWizard) return;

        const updatedSteps = {
          ...activeWizard.steps,
          [step]: {
            ...activeWizard.steps[step],
            status: 'confirmed' as const,
            confirmedAt: new Date(),
          },
        };

        // Auto-advance to next step
        const stepOrder = getStepOrder();
        const currentIndex = stepOrder.indexOf(step);
        let nextStep = activeWizard.currentStep;

        if (currentIndex < stepOrder.length - 1) {
          nextStep = stepOrder[currentIndex + 1];
          updatedSteps[nextStep] = {
            ...updatedSteps[nextStep],
            status: 'active',
          };
        }

        set({
          activeWizard: {
            ...activeWizard,
            currentStep: nextStep,
            steps: updatedSteps,
            lastModifiedAt: new Date(),
          },
        });
      },

      editStep: (step) => {
        const { activeWizard } = get();
        if (!activeWizard) return;

        // Reset step status to active for editing
        const updatedSteps = {
          ...activeWizard.steps,
          [step]: {
            ...activeWizard.steps[step],
            status: 'active' as const,
          },
        };

        set({
          activeWizard: {
            ...activeWizard,
            currentStep: step,
            steps: updatedSteps,
            lastModifiedAt: new Date(),
          },
        });
      },

      setValidation: (result) => {
        const { activeWizard } = get();
        if (!activeWizard) return;

        set({
          activeWizard: {
            ...activeWizard,
            validation: result,
            lastModifiedAt: new Date(),
          },
        });
      },

      fixIssue: (issueId) => {
        const { activeWizard } = get();
        if (!activeWizard?.validation) return;

        // Remove the fixed issue from validation
        const updatedIssues = activeWizard.validation.issues.filter(i => i.id !== issueId);
        const errorCount = updatedIssues.filter(i => i.severity === 'error').length;

        set({
          activeWizard: {
            ...activeWizard,
            validation: {
              ...activeWizard.validation,
              issues: updatedIssues,
              passed: errorCount === 0,
              score: Math.min(100, activeWizard.validation.score + 5),
            },
            lastModifiedAt: new Date(),
          },
        });
      },

      saveDraft: () => {
        const { activeWizard, drafts } = get();
        if (!activeWizard) return;

        // Remove existing draft with same ID
        const filteredDrafts = drafts.filter(d => d.id !== activeWizard.id);

        set({
          drafts: [...filteredDrafts, activeWizard],
        });
      },

      loadDraft: (draftId) => {
        const { drafts } = get();
        const draft = drafts.find(d => d.id === draftId);

        if (draft) {
          set({ activeWizard: { ...draft, lastModifiedAt: new Date() } });
        }
      },

      deleteDraft: (draftId) => {
        const { drafts } = get();
        set({ drafts: drafts.filter(d => d.id !== draftId) });
      },

      setCampaignId: (campaignId) => {
        const { activeWizard } = get();
        if (!activeWizard) return;

        set({
          activeWizard: {
            ...activeWizard,
            campaignId,
            lastModifiedAt: new Date(),
          },
        });
      },

      completeWizard: () => {
        const { activeWizard, drafts } = get();
        if (!activeWizard) return;

        // Remove from drafts if exists
        const filteredDrafts = drafts.filter(d => d.id !== activeWizard.id);

        // Update step status
        const updatedSteps = {
          ...activeWizard.steps,
          complete: {
            ...activeWizard.steps.complete,
            status: 'confirmed' as const,
            confirmedAt: new Date(),
          },
        };

        set({
          activeWizard: {
            ...activeWizard,
            currentStep: 'complete',
            steps: updatedSteps,
            lastModifiedAt: new Date(),
          },
          drafts: filteredDrafts,
        });
      },

      getCurrentStepIndex: () => {
        const { activeWizard } = get();
        if (!activeWizard) return 0;

        const stepOrder = getStepOrder();
        return stepOrder.indexOf(activeWizard.currentStep);
      },

      getProgress: () => {
        const { activeWizard } = get();
        if (!activeWizard) return { current: 0, total: 0, percentage: 0 };

        const confirmedCount = Object.values(activeWizard.steps).filter(
          s => s.status === 'confirmed'
        ).length;
        const total = CAMPAIGN_WIZARD_STEPS.filter(s => s.required).length;

        return {
          current: confirmedCount,
          total,
          percentage: Math.round((confirmedCount / total) * 100),
        };
      },

      isStepConfirmed: (step) => {
        const { activeWizard } = get();
        if (!activeWizard) return false;
        return activeWizard.steps[step].status === 'confirmed';
      },

      canProceed: () => {
        const { activeWizard } = get();
        if (!activeWizard) return false;

        const stepDef = CAMPAIGN_WIZARD_STEPS.find(s => s.id === activeWizard.currentStep);

        if (!stepDef) return false;

        // For validation step, check if validation passed
        if (activeWizard.currentStep === 'validation') {
          return activeWizard.validation?.passed ?? false;
        }

        // For other steps, check if required data is present
        return true; // Simplified - in production, add proper validation
      },
    }),
    {
      name: 'pai-campaign-wizard',
      partialize: (state) => ({
        drafts: state.drafts,
        // Don't persist activeWizard - it should be restored from drafts
      }),
    }
  ),
  shallow
);

export default useWizardStore;

